/**
 * Currency utilities for formatting and symbol conversion
 */

/**
 * Map of currency codes to their symbols
 */
const currencySymbols: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  JPY: '¥',
  CNY: '¥',
  AUD: 'A$',
  CAD: 'C$',
  CHF: 'CHF',
  INR: '₹',
  BRL: 'R$',
  MXN: 'MX$',
  ZAR: 'R',
  SEK: 'kr',
  NOK: 'kr',
  DKK: 'kr',
  PLN: 'zł',
  CZK: 'Kč',
  HUF: 'Ft',
  RUB: '₽',
  TRY: '₺',
  AED: 'د.إ',
  SAR: '﷼',
  SGD: 'S$',
  NZD: 'NZ$',
  THB: '฿',
  KRW: '₩'
};

/**
 * Get the currency symbol for a given currency code
 * @param currencyCode - ISO 4217 currency code (e.g., 'USD', 'EUR')
 * @returns Currency symbol (e.g., '$', '€')
 */
export function getCurrencySymbol(currencyCode: string | null | undefined): string {
  if (!currencyCode) return '$'; // Default to USD

  const code = currencyCode.toUpperCase();
  return currencySymbols[code] || code; // Return symbol or code if not found
}

/**
 * Format a price with the appropriate currency symbol
 * @param amount - The amount to format
 * @param currencyCode - ISO 4217 currency code (e.g., 'USD', 'EUR')
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted price string (e.g., '$123.45', '€123.45')
 */
export function formatCurrency(
  amount: number | null | undefined,
  currencyCode: string | null | undefined = 'USD',
  decimals: number = 2
): string {
  const value = amount ?? 0;
  const symbol = getCurrencySymbol(currencyCode);

  return `${symbol}${value.toFixed(decimals)}`;
}
